import { getStoryblokApi } from "@storyblok/react/rsc";

import { findTopic } from "./faq-topics";
import type { Region } from "./region";
import { storyblokCache } from "./storyblok-cache";

type RichTextNode = {
  type: string;
  content?: RichTextNode[];
  text?: string;
  marks?: { type: string }[];
};

export type FaqContent = {
  answer?: RichTextNode;
  topic?: string;
  regions?: string[];
};

export type FaqStory = {
  uuid: string;
  name: string;
  slug: string;
  content: FaqContent;
};

export type StoryblokAsset = { filename?: string; alt?: string };

export type StoryblokLink = {
  url?: string;
  cached_url?: string;
  linktype?: "story" | "url" | "email" | "asset";
  target?: string;
};

export type FaqTopicTile = {
  _uid: string;
  icon?: string;
  label?: string;
  topic?: string;
};

export type FaqQuickLink = {
  _uid: string;
  title?: string;
  description?: string;
  link?: StoryblokLink;
};

export type FaqSettings = {
  title?: string;
  subtitle?: string;
  search_placeholder?: string;
  popular_topics_label?: string;
  popular_topics?: FaqTopicTile[];
  quick_links_label?: string;
  quick_links?: FaqQuickLink[];
};

export function topicSlug(topic: string | undefined): string {
  // Topic is a canonical slug; fall back to kebab-case for legacy values.
  if (!topic) return "";
  const candidate = topic.trim();
  if (findTopic(candidate)) return candidate;
  return candidate
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

export function topicUrl(topic: string, region: Region): string {
  const prefix = region === "uk" ? "/uk" : "";
  return `${prefix}/faqs/${topic}/`;
}

// Strips folder prefix so the anchor matches DOM ids on the topic page.
export function faqAnchor(fullSlug: string): string {
  return fullSlug
    .replace(/^settings\/faqs\//, "")
    .replace(/^faqs\//, "");
}

export function faqUrl(
  faq: { slug: string; content?: { topic?: string } },
  region: Region,
): string {
  const slug = faqAnchor(faq.slug);
  const topic = topicSlug(faq.content?.topic) || "general";
  return `${topicUrl(topic, region)}#${slug}`;
}

// Defaults to IE-only when `regions` is unset.
export function faqInRegion(faq: FaqStory, region: Region): boolean {
  const regions = faq.content?.regions;
  if (!Array.isArray(regions) || regions.length === 0) return region === "ie";
  return regions.some((r) => r.toLowerCase() === region);
}

export function filterFaqsByTopicAndRegion(
  faqs: FaqStory[],
  topic: string,
  region: Region,
): FaqStory[] {
  return faqs.filter(
    (f) => topicSlug(f.content?.topic) === topic && faqInRegion(f, region),
  );
}

export function richTextToPlainText(node: RichTextNode | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.text ?? "";
  if (node.type === "hard_break") return "\n";

  const children = node.content?.map(richTextToPlainText) ?? [];

  if (node.type === "paragraph" || node.type === "list_item") {
    return children.join("");
  }
  if (node.type === "bullet_list" || node.type === "ordered_list") {
    return children
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => `• ${c}`)
      .join("\n");
  }
  return children
    .map((c) => c.trim())
    .filter(Boolean)
    .join("\n\n");
}

export function faqPageSchema(faqs: FaqStory[], topic: string, region: Region) {
  const topicFaqs = filterFaqsByTopicAndRegion(faqs, topic, region);
  if (topicFaqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: topicFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.name,
      acceptedAnswer: {
        "@type": "Answer",
        text: richTextToPlainText(faq.content?.answer),
      },
    })),
  };
}

export async function fetchAllFaqs(isDraft = false): Promise<FaqStory[]> {
  const all: FaqStory[] = [];
  const api = getStoryblokApi();
  try {
    let page = 1;
    while (true) {
      const { data } = await api.get(
        "cdn/stories",
        {
          starts_with: "settings/faqs/",
          filter_query: { component: { in: "faq" } },
          per_page: 100,
          page,
          version: isDraft ? "draft" : "published",
        },
        storyblokCache(isDraft, ["faqs"]),
      );
      const stories = (data?.stories || []) as FaqStory[];
      all.push(...stories);
      if (stories.length < 100) break;
      page++;
      if (page > 10) break; // safety cap
    }
  } catch {
    return [];
  }
  return all;
}

export async function fetchFaqs(
  region: Region,
  isDraft = false,
): Promise<FaqStory[]> {
  const all = await fetchAllFaqs(isDraft);
  return all.filter((f) => faqInRegion(f, region));
}

export async function fetchFaqSettings(
  isDraft = false,
): Promise<FaqSettings | null> {
  try {
    const { data } = await getStoryblokApi().get(
      "cdn/stories/settings/faq-drawer",
      { version: isDraft ? "draft" : "published" },
      storyblokCache(isDraft, ["faq-settings"]),
    );
    return (data?.story?.content as FaqSettings) ?? null;
  } catch {
    return null;
  }
}
